import AppText from "@common/component/AppText";
import { ColumnType } from "@common/component/InventoryTable";
import { carDetailsToString, showAlert, withOpacity } from "@common/helper";
import { capitalizeFirstLetter, HOST, makePostApiRequest } from "@common/index";
import { THEME_COLORS } from "@common/themes";
import { History, Refresh } from "@mui/icons-material";
import { CircularProgress, IconButton } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import moment from "moment";
import { StyleSheet, View } from "react-native";

import queryClient from "@src/reactQuery";
import { InventoryListingItem } from "../apiService";

type ResponseType = {
  success: boolean;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    fullname: string;
    username: string;
  };
  display_message: string;
  message: string;
};

const TechnicianCell = ({
  isReAssignable,
  techOrInsp = "",
  outboundJourneyId,
}: {
  isReAssignable: boolean;
  techOrInsp: string;
  outboundJourneyId: number;
}) => {
  const { isPending, mutate } = useMutation({
    mutationFn: async () => {
      const response = await makePostApiRequest<ResponseType>(
        `${HOST}/external/outbound/assign-or-reassign-inspection`,
        {
          outbound_journey_id: outboundJourneyId,
          //TODO: hardcoded for now
          reassign_reason: "Reassign",
          reassign_reason_id: 1,
        },
        {},
        "Keycloak-Authorization",
        "bearer",
        "refurb"
      );

      return response.data;
    },
    onSuccess(data) {
      showAlert(data.display_message ?? `Tech/Insp reassigned`, "info");
      queryClient.invalidateQueries({ queryKey: ["outboundListing"] });
    },
    onError(e) {
      const msg = (isAxiosError(e) && e.response?.data?.display_message) || "Failed to reassign Tech/Insp";
      showAlert(msg, "error");
    },
  });

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    mutate();
  };

  if (isPending)
    return (
      <View style={{ alignItems: "center", justifyContent: "center" }}>
        <CircularProgress color="primary" size={24} />
      </View>
    );

  return (
    <View style={{ flexDirection: "row", alignItems: "center", columnGap: 8 }}>
      {isReAssignable ? (
        <IconButton onClick={handleClick} size="small">
          <Refresh
            sx={{
              color: THEME_COLORS.purple,
              backgroundColor: withOpacity("#7D7D7D", 0.08),
              border: `8px solid transparent`,
              borderRadius: 999,
            }}
          />
        </IconButton>
      ) : null}

      <AppText fontFamily="regular" fontSize={14}>
        {isReAssignable && !techOrInsp ? <AppText color={THEME_COLORS.purple}>Assign</AppText> : techOrInsp || "--"}
      </AppText>
    </View>
  );
};

const cityColumnData = ({
  onClickHistoryIcon,
}: {
  onClickHistoryIcon: (row: InventoryListingItem) => void;
}): ColumnType<InventoryListingItem>[] => [
  {
    name: "LEAD ID",
    component: ({ row }) => {
      const leadId = row.lead.id;
      const createdAt = row.lead.created_at;

      return (
        <View style={styles.rowGap2}>
          <AppText allowTextCopy fontFamily="semibold" fontSize={14}>
            {leadId}
          </AppText>
          <AppText fontFamily="regular" fontSize={12} color={THEME_COLORS.sideMenuGrey}>
            Created: {moment(createdAt).format("Do MMM 'YY, h:mm A")}
          </AppText>
        </View>
      );
    },
    label: "",
  },
  {
    name: "CAR DETAILS",
    component: ({ row }) => {
      const carReg = row.car_details.registration_no;
      const carDetails = carDetailsToString(row.car_details);

      return (
        <View style={styles.rowGap2}>
          <AppText fontFamily="semibold" fontSize={14}>
            {carReg}
          </AppText>
          <AppText addToolTip fontFamily="regular" fontSize={12} color={THEME_COLORS.sideMenuGrey}>
            {carDetails}
          </AppText>
        </View>
      );
    },
    label: "",
  },
  {
    name: "PROC CATEGORY",
    component: ({ row }) => {
      const procCat = row.car_details.proc_category;

      return (
        <View style={styles.rowGap2}>
          <AppText addToolTip fontFamily="semibold" fontSize={14}>
            {capitalizeFirstLetter(procCat)}
          </AppText>
        </View>
      );
    },
    label: "",
  },
  {
    name: "WORK TYPE",
    component: ({ row }) => {
      const lastWork = row.last_work_type;

      return (
        <View>
          <AppText addToolTip fontFamily="regular" fontSize={14}>
            {lastWork}
          </AppText>
        </View>
      );
    },
    label: "",
  },
  {
    name: "WORKSHOP",
    component: ({ row }) => {
      const workshop = row.workshop;

      return (
        <View>
          <AppText addToolTip fontFamily="regular" fontSize={14}>
            {workshop}
          </AppText>
        </View>
      );
    },
    label: "",
  },
  {
    name: "TECH/INSP",
    component: ({ row }) => {
      const techOrInsp = row.technician_or_inspector?.trim();
      const isReAssignable = !!row.is_re_assignable;
      const outboundJourneyId = row.outbound_car_details.outbound_journey_id;

      console.log("************render************");

      return (
        <TechnicianCell isReAssignable={isReAssignable} techOrInsp={techOrInsp} outboundJourneyId={outboundJourneyId} />
      );
    },
    label: "",
  },
  {
    name: "CURRENT TASK",
    component: ({ row }) => {
      const currentTask = row.current_task;

      return (
        <View>
          <AppText addToolTip fontFamily="semibold" fontSize={14}>
            {currentTask}
          </AppText>
        </View>
      );
    },
    label: "",
  },
  {
    name: "CURRENT TASK TAT",
    component: ({ row }) => {
      const today = moment(Date.now());
      const assignedAt = moment(row.task_triggered_at);

      const tatInDays = today.diff(assignedAt, "days") + " days";
      const since = "since " + assignedAt.format("Do MMM 'YY, h:mm A");

      return (
        <View>
          <AppText fontFamily="semibold" fontSize={14}>
            {tatInDays}
          </AppText>
          <AppText fontFamily="regular" fontSize={12} color={THEME_COLORS.sideMenuGrey}>
            {since}
          </AppText>
        </View>
      );
    },
    sortable: true,
    label: "task_triggered_at",
  },
  {
    name: "STATUS",
    component: ({ row }) => {
      const status = row.status.name;
      const statusColor = row.status.colour_coding;
      const updatedAt = moment(row?.last_status_updated_at).format("Do MMM 'YY, h:mm A")

      const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
        event.stopPropagation();
        onClickHistoryIcon?.(row);
      };

      return (
        <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", columnGap: 4 }}>
          <View style={{ rowGap: 4 }}>
            <View
              style={{
                width: "auto",
                borderRadius: 5,
                paddingVertical: 4,
                paddingHorizontal: 12,
                backgroundColor: withOpacity(statusColor, 0.3),
              }}
            >
              <AppText fontFamily="bold" fontSize={12} color={statusColor} textAlign="center">
                {status}
              </AppText>
            </View>
            <AppText fontFamily="regular" fontSize={12} color={THEME_COLORS.sideMenuGrey} textAlign="center">
              Updated: {updatedAt}
            </AppText>
          </View>
          <IconButton onClick={handleClick} size="small">
            <History
              sx={{
                color: THEME_COLORS.purple,
                backgroundColor: withOpacity("#7D7D7D", 0.08),
                border: `8px solid transparent`,
                borderRadius: 999,
              }}
            />
          </IconButton>
        </View>
      );
    },
    label: "",
  },
];

const workshopColumnData = ({
  onClickHistoryIcon,
}: {
  onClickHistoryIcon: (row: InventoryListingItem) => void;
}): ColumnType<InventoryListingItem>[] => [
  {
    name: "LEAD ID",
    component: ({ row }) => {
      const leadId = row.lead.id;

      return (
        <View>
          <AppText allowTextCopy fontFamily="semibold" fontSize={14}>
            {leadId}
          </AppText>
        </View>
      );
    },
    label: "",
  },
  {
    name: "CAR DETAILS",
    component: ({ row }) => {
      const carReg = row.car_details.registration_no;
      const carDetails = carDetailsToString(row.car_details);

      return (
        <View style={styles.rowGap2}>
          <AppText fontFamily="semibold" fontSize={14}>
            {carReg}
          </AppText>
          <AppText addToolTip fontFamily="regular" fontSize={12} color={THEME_COLORS.sideMenuGrey}>
            {carDetails}
          </AppText>
        </View>
      );
    },
    label: "",
  },
  {
    name: "PROC CATEGORY",
    component: ({ row }) => {
      const procCat = row.car_details.proc_category;

      return (
        <View style={styles.rowGap2}>
          <AppText addToolTip fontFamily="semibold" fontSize={14}>
            {capitalizeFirstLetter(procCat)}
          </AppText>
        </View>
      );
    },
    label: "",
  },
  {
    name: "CREATED ON",
    component: ({ row }) => {
      const createdAt = moment(row.lead.created_at);

      const relativeTimestamp = createdAt.fromNow();
      const since = "since " + createdAt.format("Do MMM 'YY, h:mm A");

      return (
        <View>
          <AppText fontFamily="semibold" fontSize={14}>
            {relativeTimestamp}
          </AppText>
          <AppText fontFamily="regular" fontSize={12} color={THEME_COLORS.sideMenuGrey}>
            {since}
          </AppText>
        </View>
      );
    },
    sortable: true,
    label: "created_at",
  },
  {
    name: "TECH/INSP",
    component: ({ row }) => {
      const techOrInsp = row.technician_or_inspector?.trim();
      const isReAssignable = !!row.is_re_assignable;
      const outboundJourneyId = row.outbound_car_details.outbound_journey_id;

      console.log("************render************");

      return (
        <TechnicianCell isReAssignable={isReAssignable} techOrInsp={techOrInsp} outboundJourneyId={outboundJourneyId} />
      );
    },
    label: "",
  },
  {
    name: "STATUS UPDATE SINCE",
    component: ({ row }) => {
      const updatedAt = moment(row.last_status_updated_at);

      const relativeTimestamp = updatedAt.fromNow();
      const since = updatedAt.format("Do MMM 'YY, h:mm A");

      return (
        <View>
          <AppText fontFamily="semibold" fontSize={14}>
            {relativeTimestamp}
          </AppText>
          <AppText fontFamily="regular" fontSize={12} color={THEME_COLORS.sideMenuGrey}>
            {since}
          </AppText>
        </View>
      );
    },
    sortable: true,
    label: "last_status_updated_at",
  },
  {
    name: "STATUS",
    component: ({ row }) => {
      const status = row.status.name;
      const statusColor = row.status.colour_coding;
      const updatedAt = moment(row?.last_status_updated_at).format("Do MMM 'YY, h:mm A");

      const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
        event.stopPropagation();
        onClickHistoryIcon?.(row);
      };

      return (
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
          <View style={{ alignItems: "flex-start", rowGap: 4 }}>
            <View
              style={{
                width: "auto",
                borderRadius: 5,
                paddingVertical: 4,
                paddingHorizontal: 12,
                backgroundColor: withOpacity(statusColor, 0.3),
              }}
            >
              <AppText fontFamily="bold" fontSize={12} color={statusColor} textAlign="center">
                {status}
              </AppText>
            </View>
            <AppText fontFamily="regular" fontSize={12} color={THEME_COLORS.sideMenuGrey} textAlign="center">
              Updated: {updatedAt}
            </AppText>
          </View>
          <IconButton onClick={handleClick} size="small">
            <History
              sx={{
                color: THEME_COLORS.purple,
                backgroundColor: withOpacity("#7D7D7D", 0.08),
                border: `8px solid transparent`,
                borderRadius: 999,
              }}
            />
          </IconButton>
        </View>
      );
    },
    label: "",
  },
];

export { cityColumnData, workshopColumnData };

const styles = StyleSheet.create({
  rowGap2: {
    gap: 2,
  },
});
